import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

// Import only essential components synchronously
import ErrorBoundary from './components/ErrorBoundary';
import { CompanyProvider } from './contexts/CompanyContext';
import { AuthProvider } from './hooks/useAuth';

// Import layout components (these are small and used frequently)
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import BillingRouteGuard from './components/billing/BillingRouteGuard';
import AuthLayout from './components/layouts/AuthLayout';
import DashboardLayout from './components/layouts/DashboardLayout';
import MainLayout from './components/layouts/MainLayout';
import { SystemAdminLayout } from './components/layouts/SystemAdminLayout';
import { MaintenanceWrapper } from './components/MaintenanceWrapper';
import { IntercomMessenger } from './components/IntercomMessenger';
import { ScrollToTop } from './components/ScrollToTop';

// Lazy load all page components to dramatically improve initial load time
const Index = React.lazy(() => import('./pages/Index'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const CreatePosition = React.lazy(() => import('./pages/CreatePosition'));
const EditPosition = React.lazy(() => import('./pages/EditPosition'));
const InterviewRoom = React.lazy(() => import('./pages/InterviewRoom'));
const Candidate = React.lazy(() => import('./pages/Candidate'));
const AddCandidate = React.lazy(() => import('./pages/AddCandidate'));
const CandidateProfile = React.lazy(() => import('./pages/CandidateProfile'));
const CompanySettings = React.lazy(() => import('./pages/CompanySettings'));
const Positions = React.lazy(() => import('./pages/Positions'));
const PositionDetail = React.lazy(() => import('./pages/PositionDetail'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Companies = React.lazy(() => import('./pages/Companies'));
const EditCompany = React.lazy(() => import('./pages/EditCompany'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const ForcedPasswordReset = React.lazy(() => import('./pages/ForcedPasswordReset'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const EmployerTerms = React.lazy(() => import('./pages/EmployerTerms'));
const CandidateTerms = React.lazy(() => import('./pages/CandidateTerms'));
const TenantOnboarding = React.lazy(() =>
  import('./pages/TenantOnboarding').then(module => ({ default: module.TenantOnboarding }))
);
const CompanySetupWizard = React.lazy(() =>
  import('./pages/CompanySetupWizard').then(module => ({ default: module.CompanySetupWizard }))
);
const InterviewInvitationLanding = React.lazy(() => import('./pages/InterviewInvitationLanding'));
const InterviewCompletion = React.lazy(() => import('./pages/InterviewCompletion'));
const JobBoard = React.lazy(() => import('./pages/JobBoard'));
const JobDetails = React.lazy(() => import('./pages/JobDetails'));
const ChoosePlan = React.lazy(() => import('./pages/ChoosePlan'));
const ApiDocs = React.lazy(() => import('./pages/ApiDocs'));

// Lazy load System Admin pages (these use named exports)
const SystemAdminDashboard = React.lazy(() =>
  import('./pages/system-admin/SystemAdminDashboard').then(module => ({
    default: module.SystemAdminDashboard,
  }))
);
const TenantsManagement = React.lazy(() =>
  import('./pages/system-admin/TenantsManagement').then(module => ({
    default: module.TenantsManagement,
  }))
);
const SystemAdminUsersManagement = React.lazy(() =>
  import('./pages/system-admin/UsersManagement').then(module => ({
    default: module.SystemAdminUsersManagement,
  }))
);
const SystemAdminCompaniesManagement = React.lazy(() =>
  import('./pages/system-admin/CompaniesManagement').then(module => ({
    default: module.SystemAdminCompaniesManagement,
  }))
);
const SystemAdminPositionsManagement = React.lazy(() =>
  import('./pages/system-admin/PositionsManagement').then(module => ({
    default: module.SystemAdminPositionsManagement,
  }))
);
const SystemAdminCandidatesManagement = React.lazy(() =>
  import('./pages/system-admin/CandidatesManagement').then(module => ({
    default: module.SystemAdminCandidatesManagement,
  }))
);
const SystemAdminBlockedEmailsManagement = React.lazy(() =>
  import('./pages/system-admin/BlockedEmailsManagement').then(module => ({
    default: module.SystemAdminBlockedEmailsManagement,
  }))
);
const SystemAdminSessionsManagement = React.lazy(() =>
  import('./pages/system-admin/SessionsManagement').then(module => ({
    default: module.SystemAdminSessionsManagement,
  }))
);
const SystemAdminBillingManagement = React.lazy(() =>
  import('./pages/system-admin/BillingManagement').then(module => ({
    default: module.SystemAdminBillingManagement,
  }))
);
const SystemAdminInvitations = React.lazy(() =>
  import('./pages/system-admin/InvitationsManagement').then(module => ({
    default: module.SystemAdminInvitations,
  }))
);
const SystemAdminSettings = React.lazy(() =>
  import('./pages/system-admin/SystemSettings').then(module => ({
    default: module.SystemAdminSettings,
  }))
);
const SystemAdminNotificationsManagement = React.lazy(() =>
  import('./pages/system-admin/NotificationsManagement').then(module => ({
    default: module.SystemAdminNotificationsManagement,
  }))
);
const SystemAdminProfile = React.lazy(() =>
  import('./pages/system-admin/SystemAdminProfile').then(module => ({
    default: module.SystemAdminProfile,
  }))
);

// Loading component for lazy-loaded routes
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <AuthProvider>
            <IntercomMessenger appId="cyuwbs0e" />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* API Documentation - standalone route, not in any layout */}
                <Route path="/api-docs" element={<ApiDocs />} />

                {/* Public routes with MainLayout - NOT affected by maintenance mode */}
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms/employer" element={<EmployerTerms />} />
                  <Route path="/terms/candidate" element={<CandidateTerms />} />
                  <Route path="/jobs" element={<JobBoard />} />
                  <Route path="/jobs/:companyHandle" element={<JobBoard />} />
                  <Route path="/jobs/:companyHandle/:positionSlug" element={<JobDetails />} />
                  <Route path="/job/:positionId" element={<JobDetails />} />
                  {/* Plan selection should be available post-signup and for logged-in users; component self-guards auth */}
                  <Route path="/choose-plan" element={<ChoosePlan />} />
                </Route>

                {/* Auth routes with AuthLayout - NOT affected by maintenance mode */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/onboarding" element={<TenantOnboarding />} />
                </Route>

                {/* Reset password route - standalone so authenticated users can access it */}
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Forced password reset route - NOT affected by maintenance mode */}
                <Route path="/forced-password-reset" element={<ForcedPasswordReset />} />

                {/* System Admin routes with SystemAdminLayout - System admins can bypass maintenance mode */}
                <Route
                  element={
                    <MaintenanceWrapper>
                      <ProtectedRoute requireSystemAdmin>
                        <SystemAdminLayout />
                      </ProtectedRoute>
                    </MaintenanceWrapper>
                  }
                >
                  <Route path="/system-admin" element={<SystemAdminDashboard />} />
                  <Route path="/system-admin/tenants" element={<TenantsManagement />} />
                  <Route path="/system-admin/users" element={<SystemAdminUsersManagement />} />
                  <Route
                    path="/system-admin/companies"
                    element={<SystemAdminCompaniesManagement />}
                  />
                  <Route
                    path="/system-admin/positions"
                    element={<SystemAdminPositionsManagement />}
                  />
                  <Route
                    path="/system-admin/candidates"
                    element={<SystemAdminCandidatesManagement />}
                  />
                  <Route
                    path="/system-admin/candidates/blocked-emails"
                    element={<SystemAdminBlockedEmailsManagement />}
                  />
                  <Route
                    path="/system-admin/interviews"
                    element={<SystemAdminSessionsManagement />}
                  />

                  <Route path="/system-admin/billing" element={<SystemAdminBillingManagement />} />
                  <Route path="/system-admin/invitations" element={<SystemAdminInvitations />} />
                  <Route path="/system-admin/profile" element={<SystemAdminProfile />} />
                  <Route path="/system-admin/settings" element={<SystemAdminSettings />} />
                  <Route
                    path="/system-admin/notifications"
                    element={<SystemAdminNotificationsManagement />}
                  />
                </Route>

                {/* Regular authenticated routes with DashboardLayout - AFFECTED by maintenance mode */}
                <Route element={<MaintenanceWrapper />}>
                  <Route
                    element={
                      <CompanyProvider>
                        <ProtectedRoute>
                          <DashboardLayout />
                        </ProtectedRoute>
                      </CompanyProvider>
                    }
                  >
                    <Route element={<BillingRouteGuard />}>
                      <Route path='/dashboard' element={<Dashboard />} />
                      <Route path='/companies' element={<Companies />} />
                      <Route path='/positions' element={<Positions />} />
                      <Route path='/positions/:id' element={<PositionDetail />} />
                      <Route path='/candidates' element={<Candidate />} />
                      <Route path='/candidates/:id' element={<CandidateProfile />} />
                      <Route path='/profile' element={<Profile />} />
                      {/* Redirect /settings to /company-settings */}
                      <Route
                        path='/settings'
                        element={
                          <Navigate to='/company-settings' replace />
                        }
                      />
                      <Route
                        path='/company-settings'
                        element={
                          <ProtectedRoute allowedRoles={['system_admin', 'tenant_admin']}>
                            <CompanySettings />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/create-position" element={<CreatePosition />} />
                      <Route path="/positions/:id/edit" element={<EditPosition />} />
                      <Route path="/new-company" element={<CompanySetupWizard />} />
                      <Route path="/companies/new" element={<CompanySetupWizard />} />
                      <Route path="/companies/:id/edit" element={<EditCompany />} />
                      <Route path="/add-candidate" element={<AddCandidate />} />
                      <Route
                        path="/company-setup"
                        element={<CompanySetupWizard isOnboarding={true} />}
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Interview-related routes - NOW affected by maintenance mode */}
                <Route
                  path="/interview-invitation/:invitationId"
                  element={
                    <MaintenanceWrapper>
                      <InterviewInvitationLanding />
                    </MaintenanceWrapper>
                  }
                />
                <Route
                  path="/interview-room/:invitationId"
                  element={
                    <MaintenanceWrapper>
                      <InterviewRoom />
                    </MaintenanceWrapper>
                  }
                />

                <Route
                  path="/interview-completion/:sessionId"
                  element={
                    <MaintenanceWrapper>
                      <InterviewCompletion />
                    </MaintenanceWrapper>
                  }
                />

                {/* 404 route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
